import { env } from '@server/shared/constants/env'

import type { Params } from '../types'

const DELEGATED_SYSTEM_PATHS = new Set([
  '/api/v1/auth/internal/access-token',
  '/api/v1/user/podo/isExists',
  '/api/v1/user/podo',
])

/** Parse one Bearer challenge; quoted descriptions cannot supply an error parameter. */
export function hasInvalidBearerChallenge(challenge: string | null): boolean {
  if (!challenge || challenge.length > 4096 || /[^\t\x20-\x7e]/.test(challenge)) return false
  const scheme = /^Bearer[ \t]+/i.exec(challenge.trim())
  if (!scheme) return false
  const parameters = challenge.trim().slice(scheme[0].length)
  const parameter =
    /[ \t]*([!#$%&'*+\-.^_`|~0-9A-Za-z]+)[ \t]*=[ \t]*(?:"((?:[\x20-\x21\x23-\x5b\x5d-\x7e]|\\[\t\x20-\x7e])*)"|([!#$%&'*+\-.^_`|~0-9A-Za-z]+))[ \t]*/gy
  const seen = new Set<string>()
  let invalidToken = false
  let cursor = 0
  while (cursor < parameters.length) {
    parameter.lastIndex = cursor
    const parsed = parameter.exec(parameters)
    if (!parsed) return false
    const name = parsed[1].toLowerCase()
    if (seen.has(name)) return false
    seen.add(name)
    if (name === 'error') invalidToken = (parsed[2] ?? parsed[3]) === 'invalid_token'
    cursor = parameter.lastIndex
    if (cursor === parameters.length) return invalidToken
    if (parameters[cursor] !== ',' || ++cursor === parameters.length) return false
  }
  return false
}

/** Only the configured physical server response may authorize one safe re-exchange. */
export function isTrustedBackendBearerRejection(response: Response, params: Params, requestUrl: URL): boolean {
  if (
    typeof window !== 'undefined' ||
    params.method?.toUpperCase() !== 'POST' ||
    !DELEGATED_SYSTEM_PATHS.has(requestUrl.pathname) ||
    requestUrl.protocol !== 'https:' ||
    requestUrl.username ||
    requestUrl.password ||
    requestUrl.search ||
    requestUrl.hash ||
    response.status !== 401 ||
    response.redirected !== false ||
    response.url !== requestUrl.href ||
    !/^Bearer[ \t]+[A-Za-z0-9\-._~+/]+=*$/.test(new Headers(params.header).get('Authorization') ?? '')
  )
    return false

  const trusted = [env.PODO_API_URL, env.NEXT_PUBLIC_PODO_API_URL].some((value) => {
    try {
      const backend = new URL(value)
      return (
        backend.protocol === 'https:' &&
        !backend.username &&
        !backend.password &&
        !backend.search &&
        !backend.hash &&
        backend.origin === requestUrl.origin
      )
    } catch {
      return false
    }
  })
  return trusted && hasInvalidBearerChallenge(response.headers.get('WWW-Authenticate'))
}
